import React, { useState } from 'react';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import { auth, firestore } from './firebaseConfig';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';
import './LoginView.css';

export default function LoginView({ onClose = () => {}, onGuest = () => {}, embedded = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const destination = location.state?.from || '/feed';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const friendlyAuthError = (error) => {
    switch (error?.code) {
      case 'auth/invalid-credential':
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        return 'The email or password is incorrect.';
      case 'auth/invalid-email':
        return 'Enter a valid email address.';
      case 'auth/email-already-in-use':
        return 'An account already exists with this email.';
      case 'auth/weak-password':
        return 'Use a stronger password with at least 6 characters.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please wait a moment and try again.';
      case 'auth/network-request-failed':
        return 'A network problem interrupted sign-in. Check your connection and try again.';
      case 'auth/popup-blocked':
        return 'Your browser blocked the sign-in window. Allow popups for this site and try again.';
      case 'auth/popup-closed-by-user':
      case 'auth/cancelled-popup-request':
        return 'Google sign-in was canceled.';
      case 'auth/unauthorized-domain':
        return 'Google sign-in is not available on this website address yet.';
      case 'auth/operation-not-allowed':
        return 'Google sign-in is temporarily unavailable.';
      case 'auth/account-exists-with-different-credential':
        return 'This email is already connected to another sign-in method.';
      default:
        return 'We could not complete sign-in. Please try again.';
    }
  };

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleSubmit = async () => {
    setError('');
    setMessage('');
    if (!isValidEmail(email)) return setError('Invalid email address');
    if (password.length < 6) return setError('Password must be at least 6 characters');
    if (isRegistering && password !== confirmPassword) return setError('Passwords do not match');

    setLoading(true);
    try {
      if (isRegistering) {
        const { user } = await createUserWithEmailAndPassword(auth, email, password);
        await setDoc(doc(firestore, 'userReg', user.uid), {
          email,
          emailVerified: false,
          createdAt: new Date()
        });
        await sendEmailVerification(user);
        setMessage('Registered! Check your inbox to verify.');
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        onClose();
        navigate(destination, { replace: true });
      }
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setMessage('');
    if (!isValidEmail(email)) return setError('Enter a valid email to reset');
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage('Reset link sent! Check your email.');
    } catch (err) {
      setError(friendlyAuthError(err));
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });

      const result = await signInWithPopup(auth, provider);
      const signedInUser = result.user;
      const userRef = doc(firestore, 'userReg', signedInUser.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        await setDoc(userRef, {
          email: signedInUser.email || '',
          emailVerified: signedInUser.emailVerified,
          displayName: signedInUser.displayName || '',
          profileImageUrl: signedInUser.photoURL || '',
          createdAt: new Date(),
          provider: 'google'
        });
      }

      onClose();
      navigate(destination, { replace: true });
    } catch (err) {
      setError(friendlyAuthError(err));
      setLoading(false);
    }
  };

  const form = (
    <div className="p4r-login-form">
      {error && <Alert variant="danger">{error}</Alert>}
      {message && <Alert variant="success">{message}</Alert>}

      <Form className="p4r-login-form" onSubmit={(event) => { event.preventDefault(); handleSubmit(); }}>
        <Form.Group controlId="loginEmail">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={event => setEmail(event.target.value)}
          />
        </Form.Group>

        <Form.Group controlId="loginPassword">
          <Form.Label>Password</Form.Label>
          <Form.Control
            type="password"
            autoComplete={isRegistering ? 'new-password' : 'current-password'}
            placeholder="••••••••"
            value={password}
            onChange={event => setPassword(event.target.value)}
          />
        </Form.Group>

        {isRegistering && (
          <Form.Group controlId="confirmPassword">
            <Form.Label>Confirm password</Form.Label>
            <Form.Control
              type="password"
              autoComplete="new-password"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={event => setConfirmPassword(event.target.value)}
            />
          </Form.Group>
        )}

        <div className="p4r-login-form__mode">
          <Form.Check
            type="switch"
            id="toggleRegister"
            label={isRegistering ? 'Create a new account' : 'I need a new account'}
            checked={isRegistering}
            onChange={() => setIsRegistering(value => !value)}
          />
          {!isRegistering && (
            <Button type="button" variant="link" className="p4r-login-form__forgot" onClick={handleForgotPassword}>
              Forgot password?
            </Button>
          )}
        </div>

        <div className="p4r-login-form__actions">
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? <Spinner as="span" animation="border" size="sm" /> : isRegistering ? 'Create account' : 'Sign in'}
          </Button>

          <div className="p4r-login-form__divider">or</div>

          <Button type="button" className="p4r-login-form__google" onClick={handleGoogleLogin} disabled={loading}>
            Continue with Google
          </Button>

          <Button
            type="button"
            variant="outline-light"
            className="p4r-login-form__guest"
            onClick={() => { onClose(); onGuest(); }}
          >
            Continue as guest
          </Button>
        </div>
      </Form>
    </div>
  );

  if (embedded) return form;

  return (
    <main className="p4r-login-page">
      <section className="p4r-login-page__card" aria-label="Pool 4 Real sign in">
        <header className="p4r-login-page__heading">
          <h1>Welcome to Pool 4 Real</h1>
          <p>Sign in to continue with your player profile and shared app data.</p>
        </header>
        {form}
      </section>
    </main>
  );
}
